// User roles - only admin needs authentication, clients browse publicly
export type UserRole = 'admin'

// Permission system
export type Permission = 
  // Package management
  | 'packages:create'
  | 'packages:read'
  | 'packages:update'
  | 'packages:delete'
  | 'packages:publish'
  | 'packages:archive'
  
  // User management
  | 'users:create'
  | 'users:read'
  | 'users:update'
  | 'users:delete'
  | 'users:manage_roles'
  
  // Visa applications
  | 'visa:create'
  | 'visa:read'
  | 'visa:update'
  | 'visa:delete'
  | 'visa:approve'
  | 'visa:process'
  
  // Analytics and reports
  | 'analytics:read'
  | 'reports:generate'
  | 'reports:export'
  
  // System settings
  | 'settings:read'
  | 'settings:update'
  | 'system:backup'
  | 'system:maintenance'

// User interface
export interface User {
  id: number
  email: string
  name: string
  role: UserRole
  avatar?: string | null
  isActive: boolean
  permissions?: Permission[]
  createdAt: string
  updatedAt: string
  lastLogin?: string
}

// Authentication state
export interface AuthState {
  user: User | null
  isAuthenticated: boolean
  isLoading: boolean
  token?: string
}

// Role-based permissions mapping - only admin role exists
export const ROLE_PERMISSIONS: Record<UserRole, Permission[]> = {
  admin: [
    // Full access to everything
    'packages:create', 'packages:read', 'packages:update', 'packages:delete', 'packages:publish', 'packages:archive',
    'users:create', 'users:read', 'users:update', 'users:delete', 'users:manage_roles',
    'visa:create', 'visa:read', 'visa:update', 'visa:delete', 'visa:approve', 'visa:process',
    'analytics:read', 'reports:generate', 'reports:export',
    'settings:read', 'settings:update', 'system:backup', 'system:maintenance'
  ]
}

// Route access levels - only public and admin needed
export type RouteAccessLevel = 'public' | 'admin'

// Route configuration
export interface RouteConfig {
  path: string
  accessLevel: RouteAccessLevel
  requiredPermissions?: Permission[]
  allowedRoles?: UserRole[]
}

// Predefined route configurations
export const ROUTE_CONFIGS: RouteConfig[] = [
  // Public routes
  { path: '/', accessLevel: 'public' },
  { path: '/destination', accessLevel: 'public' },
  { path: '/visa', accessLevel: 'public' },
  { path: '/about', accessLevel: 'public' },
  { path: '/contact', accessLevel: 'public' },
  { path: '/terms-of-service', accessLevel: 'public' },
  { path: '/privacy-policy', accessLevel: 'public' },
  
  // Admin routes - only admin role needed
  { path: '/admin/login', accessLevel: 'public' },
  { path: '/admin', accessLevel: 'admin', allowedRoles: ['admin'] },
  { path: '/admin/dashboard', accessLevel: 'admin', allowedRoles: ['admin'] },
  { 
    path: '/admin/packages', 
    accessLevel: 'admin', 
    allowedRoles: ['admin'],
    requiredPermissions: ['packages:read']
  },
  { 
    path: '/admin/packages/create', 
    accessLevel: 'admin', 
    allowedRoles: ['admin'],
    requiredPermissions: ['packages:create']
  },
  { 
    path: '/admin/users', 
    accessLevel: 'admin', 
    allowedRoles: ['admin'],
    requiredPermissions: ['users:read']
  },
  { 
    path: '/admin/visa-applications', 
    accessLevel: 'admin', 
    allowedRoles: ['admin'],
    requiredPermissions: ['visa:read']
  },
  { 
    path: '/admin/settings', 
    accessLevel: 'admin', 
    allowedRoles: ['admin'],
    requiredPermissions: ['settings:read']
  }
]